import { Lock } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Shared "Show / hide columns" checklist used by BOTH the Master and Manager
// boards so their control can never drift. The board passes its own column
// list (with the sticky Project column), the current hidden set, and handlers.
// The sticky column is rendered as a locked row and can never be hidden.
export default function BoardColumnsDialog({
  open,
  onOpenChange,
  columns,
  hidden,
  onToggle,
  onShowAll,
  onResetToDefault,
  idPrefix,
  dialogTestId,
}) {
  const sticky = columns.find(([, , isSticky]) => isSticky);
  const hideable = columns.filter(([, , isSticky]) => !isSticky);
  const hiddenCount = hideable.filter(([key]) => hidden.has(key)).length;

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent data-testid={dialogTestId}>
        <DialogHeader>
          <DialogTitle>Show / hide columns</DialogTitle>
          <DialogDescription>
            Choose which columns appear on this board. Your choice is saved to your
            account and only affects your own view.
          </DialogDescription>
        </DialogHeader>
        <p style={{ fontSize: 12.5, color: "#64748b", margin: 0 }}>
          Hide the columns you don&rsquo;t need for a faster visual scan. This only
          changes your own view on this board — roles, permissions, project data
          and lifecycle are unchanged.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12, maxHeight: "50vh", overflowY: "auto" }}>
          {sticky && (
            <div
              data-testid={`${idPrefix}-columns-locked-project`}
              style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, padding: "2px 0" }}
            >
              <Lock aria-hidden size={14} style={{ color: "#94a3b8", marginTop: 2, flexShrink: 0 }} />
              <span>
                <strong>{sticky[1]}</strong>
                <span style={{ display: "block", fontSize: 11.5, color: "#94a3b8" }}>
                  Always visible — it identifies each row, so it can&rsquo;t be hidden.
                </span>
              </span>
            </div>
          )}
          {hideable.map(([key, label]) => {
            const isHidden = hidden.has(key);
            return (
              <label
                data-testid={`${idPrefix}-columns-toggle-${key}`}
                key={key}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}
              >
                <input
                  checked={!isHidden}
                  data-testid={`${idPrefix}-columns-checkbox-${key}`}
                  onChange={() => onToggle(key)}
                  type="checkbox"
                />
                <span style={{ color: isHidden ? "#94a3b8" : "#0f172a", textDecoration: isHidden ? "line-through" : "none" }}>
                  {label}
                </span>
              </label>
            );
          })}
        </div>
        <DialogFooter style={{ marginTop: 12, gap: 8, flexWrap: "wrap" }}>
          <button
            data-testid={`${idPrefix}-columns-reset`}
            disabled={hiddenCount === 0}
            onClick={onShowAll}
            style={{
              background: "transparent",
              border: "1px solid #cbd5e1",
              borderRadius: 999,
              padding: "0.35rem 0.9rem",
              fontSize: 12.5,
              color: hiddenCount === 0 ? "#94a3b8" : "#0f172a",
              cursor: hiddenCount === 0 ? "not-allowed" : "pointer",
            }}
            type="button"
          >
            Show all
          </button>
          <button
            data-testid={`${idPrefix}-columns-default`}
            onClick={onResetToDefault}
            style={{
              background: "transparent",
              border: "1px solid #cbd5e1",
              borderRadius: 999,
              padding: "0.35rem 0.9rem",
              fontSize: 12.5,
              color: "#0f172a",
              cursor: "pointer",
            }}
            type="button"
          >
            Reset to default
          </button>
          <button
            data-testid={`${idPrefix}-columns-done`}
            onClick={() => onOpenChange(false)}
            style={{
              background: "#0f172a",
              color: "#fff",
              border: "1px solid #0f172a",
              borderRadius: 999,
              padding: "0.35rem 1rem",
              fontSize: 12.5,
              cursor: "pointer",
            }}
            type="button"
          >
            Done
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
